<template>
  <div style="touch-action: none !important; height: 100vh; width: 100%; overflow-y: auto; padding: 20px">
    <v-col cols="12" class="pa-0">
      <v-list style="padding: 0" two-line>
        <v-list-item v-for="(workout, index) in form.splits.workouts" :key="index" class="pa-3 split-item mx-1">
          <v-row align="center" no-gutters style="width: 100%">
            <!-- Linke Spalte: Split-Name und Details -->
            <v-col :cols="$vuetify.breakpoint.smAndDown ? '12' : '3'" :class="{ 'mb-3': $vuetify.breakpoint.smAndDown }">
              <v-list-item-title class="white--text split-title"> Split {{ index + 1 }}: {{ workout.title }} </v-list-item-title>
              <v-list-item-subtitle class="white--text split-overview">
                Übungen: {{ workout.exercises ? workout.exercises.length : 0 }} | Sätze: {{ getTotalSets(workout) }}<br />
                Muskelgruppen:
                <span v-for="(muscle, mIndex) in getActiveMuscles(workout)" :key="mIndex">
                  {{ muscle }}<span v-if="mIndex < getActiveMuscles(workout).length - 1">, </span>
                </span>
              </v-list-item-subtitle>
            </v-col>

            <!-- Mittlere Spalte: Bild und Auswahl -->
            <v-col :cols="$vuetify.breakpoint.smAndDown ? '4' : '1'" class="ml-2" grow>
              <v-card
                class="workout-card rounded-xl pa-3"
                :style="{
                  '--card-color': workout.fixedVisualization ? workout.fixedVisualization.color : getTrainingPlanVisualization(workout.title).color,
                }">
                <v-img
                  :src="workout.fixedVisualization ? workout.fixedVisualization.img : getTrainingPlanVisualization(workout.title).img"
                  height="100%"
                  width="100%"
                  contain
                  class="d-flex justify-center align-center" />

                <!-- Auswahl-Menü zum Ändern der Visualisierung -->
                <v-menu v-model="menuOpen[index]" offset-y transition="scale-transition">
                  <template #activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" fab absolute color="white" x-small style="top: 2px; right: 2px">
                      <v-icon color="blue" small>mdi-pencil</v-icon>
                    </v-btn>
                  </template>

                  <v-list dark dense>
                    <v-list-item v-for="option in visualizationOptions" :key="option.key" @click="selectVisualization(option, workout, index)">
                      <v-list-item-avatar>
                        <v-img :src="option.img" contain />
                      </v-list-item-avatar>
                      <v-list-item-content>
                        <v-list-item-title>{{ option.text }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </v-card>
            </v-col>

            <!-- Rechte Spalte: Diagramm der aktiven Muskelgruppen -->
            <v-col :cols="$vuetify.breakpoint.smAndDown ? '7' : '5'" class="d-flex justify-end ml-auto">
              <MuscleDiagram
                noTouch
                height="230px"
                :split="index"
                :hideTagWindows="true"
                background="#252525"
                :exercises="workout.exercises"
                :activeClasses="getActiveMuscles(workout)" />
            </v-col>
          </v-row>
        </v-list-item>
      </v-list>
    </v-col>
  </div>
</template>

<script>
import MuscleDiagram from "./PlanRunInteractiveHuman.vue"

export default {
  name: "WorkoutSplitOverviewAndSubmit",
  props: {
    form: { type: Object, required: true },
    handleSubmitPlan: { type: Boolean, default: false },
  },
  components: { MuscleDiagram },
  computed: {
    isEditing() {
      return this.form && this.form._id
    },
  },
  data() {
    return {
      menuOpen: this.form.splits.workouts.map(() => false),
      visualizationOptions: [
        { key: "leg", text: "Beine", img: "/img/workout/legSplit.png", color: "#1C4966" },
        { key: "chest", text: "Brust", img: "/img/workout/chestSplit.png", color: "#02734A" },
        { key: "back", text: "Rücken", img: "/img/workout/backSplit.png", color: "#F2B950" },
        { key: "arm", text: "Arm", img: "/img/workout/armSplit.png", color: "#E5735D" },
        { key: "core", text: "Cardio", img: "/img/workout/cardioSplit.png", color: "#F7B5A7" },
        { key: "upper", text: "Oberkörper", img: "/img/workout/upperBodySplit.png", color: "#A3C6C4" },
      ],
    }
  },
  methods: {
    submitPlan() {
      // Ensure every workout has a fixed visualization before submitting
      this.form.splits.workouts.forEach((workout) => {
        if (!workout.fixedVisualization) {
          const { img, color } = this.getTrainingPlanVisualization(workout.title)
          this.$set(workout, "fixedVisualization", { img, color })
        }
      })

      // MODIFIED: Emit a different event based on whether we are editing or creating
      if (this.isEditing) {
        console.log("Updating existing plan with form data:", this.form)
        this.$emit("updatePlan", this.form)
      } else {
        console.log("Submitting new plan with form data:", this.form)
        this.$emit("submitPlan", this.form)
      }
    },
    selectVisualization(option, workout, index) {
      this.$set(workout, "fixedVisualization", { img: option.img, color: option.color })
      this.$set(this.menuOpen, index, false)
    },
    getTrainingPlanVisualization(title) {
      const armSplitKeywords = [
        "arm",
        "arme",
        "bizeps",
        "trizeps",
        "bizepstraining",
        "trizepstraining",
        "oberarm",
        "unterarm",
        "armtraining",
        "armübung",
        "armübungen",
        "arm-workout",
        "arm-muskulatur",
        "arms",
        "biceps",
        "triceps",
        "upper arm",
        "forearm",
        "arm training",
        "arm workout",
        "arm exercises",
        "arm muscles",
      ]
      const legSplitKeywords = [
        "bein",
        "beine",
        "beinarbeit",
        "unterkörper",
        "oberschenkel",
        "waden",
        "hamstrings",
        "quadrizeps",
        "beintraining",
        "beinübungen",
        "bein-workout",
        "leg",
        "legs",
        "leg day",
        "leg workout",
        "leg training",
        "thigh",
        "calves",
        "hamstrings",
        "quads",
        "lower body",
        "leg exercises",
      ]
      const chestSplitKeywords = [
        "brust",
        "push",
        "brusttraining",
        "brustübungen",
        "brust-workout",
        "pectoralis",
        "brustmuskulatur",
        "chest",
        "chest training",
        "chest workout",
        "pectoral",
        "pecs",
        "chest exercises",
      ]
      const backSplitKeywords = [
        "rücken",
        "pull",
        "rückentraining",
        "rückenübungen",
        "rücken-workout",
        "lats",
        "latissimus",
        "trapez",
        "trapezius",
        "back",
        "back training",
        "back workout",
        "upper back",
        "lower back",
        "lats",
        "latissimus",
        "trapezius",
        "back exercises",
      ]
      const shoulderSplitKeywords = [
        "schulter",
        "schultertraining",
        "schulterübungen",
        "schulter-workout",
        "deltoid",
        "deltoideus",
        "shoulder",
        "shoulder training",
        "shoulder workout",
        "deltoid",
        "deltoids",
        "shoulder exercises",
      ]
      const coreSplitKeywords = [
        "bauch",
        "bauchtraining",
        "bauchübungen",
        "bauch-workout",
        "sechpack",
        "magen",
        "core",
        "abs",
        "abdominals",
        "abs workout",
        "ab exercises",
        "sixpack",
        "ober",
      ]
      const lowerName = title.toLowerCase()
      if (legSplitKeywords.some((k) => lowerName.includes(k))) return this.visualizationOptions.find((o) => o.key === "leg")
      if (chestSplitKeywords.some((k) => lowerName.includes(k))) return this.visualizationOptions.find((o) => o.key === "chest")
      if (backSplitKeywords.some((k) => lowerName.includes(k))) return this.visualizationOptions.find((o) => o.key === "back")
      if (shoulderSplitKeywords.some((k) => lowerName.includes(k))) return this.visualizationOptions.find((o) => o.key === "upper")
      if (armSplitKeywords.some((k) => lowerName.includes(k))) return this.visualizationOptions.find((o) => o.key === "arm")
      if (coreSplitKeywords.some((k) => lowerName.includes(k))) return this.visualizationOptions.find((o) => o.key === "core")
      return this.visualizationOptions.find((o) => o.key === "upper") // Default to upper body if no match
    },
    getTotalSets(workout) {
      if (!workout.exercises) return 0
      return workout.exercises.reduce((sum, ex) => sum + (ex.sets?.length || 0), 0)
    },
    getActiveMuscles(workout) {
      const muscleSet = new Set()
      if (!workout.exercises) return []
      for (let exercise of workout.exercises) {
        if (exercise.tags) {
          for (let tag of exercise.tags) {
            if (tag.class) {
              muscleSet.add(tag.class)
            }
          }
        }
      }
      return Array.from(muscleSet)
    },
  },
  watch: {
    handleSubmitPlan(newValue) {
      console.log("oiii ha was beho bim submit", newValue)
      if (newValue === true) {
        this.$emit("resetSubmitPlan")
        this.submitPlan()
      }
    },
  },
}
</script>
<style lang="scss" scoped>
/* GRUNDLAGEN & CONTAINER */
.overview-container {
  touch-action: none !important;
  height: 100vh;
  width: 100%;
  overflow-y: auto;
  padding: 20px;
  background: #121212; // Dunklerer, moderner Hintergrund
}

/* LISTE & LISTEN-ELEMENTE */
.v-list {
  padding: 0;
}

.split-item {
  background: rgba(30, 30, 30, 0.7); // Semi-transparenter Hintergrund
  backdrop-filter: blur(15px); // "Frosted Glass" Effekt
  -webkit-backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 24px !important; // Stärker abgerundete Ecken
  padding: 24px !important;
  margin-bottom: 20px !important;
  transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
  overflow: hidden; // Wichtig für die Glow-Effekte
  position: relative; // Wichtig für die Glow-Effekte

  // Glow-Effekt, der von der Kartenfarbe abhängt
  &::before {
    content: "";
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background-image: radial-gradient(circle, color-mix(in srgb, var(--card-color, #424242) 20%, transparent 60%), transparent);
    transform: rotate(0deg);
    transition: transform 1s ease-in-out;
  }

  &:hover::before {
    transform: rotate(90deg);
  }
}

/* TITEL & TEXTSTILE */
.white--text {
  color: white !important;
}

.split-title {
  font-size: 1.5rem; // Größer für mehr Präsenz
  font-weight: 700;
  letter-spacing: -0.02em; // Moderneres Kerning
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5); // Bessere Lesbarkeit
}

.split-overview {
  font-size: 0.95rem;
  opacity: 0.8;
  line-height: 1.6; // Besserer Zeilenabstand
}

/* WORKOUT-KARTE (mit dem Körperteil-Bild) */
.workout-card {
  border-radius: 18px !important; // Passend zum neuen Stil
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  // Farbverlauf, der die Karte lebendiger macht
  background: linear-gradient(135deg, var(--card-color), color-mix(in srgb, var(--card-color) 70%, #1e1e1e 100%));

  &:hover {
    transform: scale(1.05) rotate(2deg);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.6);
  }
}

/* MUSKEL-DIAGRAMM */
.muscle-diagram-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

// Stellt sicher, dass das Diagramm im Container zentriert ist
::v-deep .MuscleDiagram {
  transform: scale(1.1); // Macht das Diagramm etwas größer
}

/* FOOTER & BUTTON */
.footer-actions {
  padding: 24px 0;
}
</style>
